import { useState } from 'react';
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react';
import {
    acceptAll,
    rejectAll,
    setConsent,
    getConsent,
    useHasConsent,
} from '../utils/consent';

/**
 * Banner zgody na cookies (GDPR/RODO + Google Consent Mode v2).
 * Pokazuje sie na pierwszej wizycie. Po wyborze chowa sie do nastepnego czyszczenia localStorage.
 * Trzy szybkie akcje + rozwijane "Dostosuj" z dwoma togglami.
 *
 * Dlaczego inline style: bezpieczenstwo - nie chce zalezec od tailwind kompilacji,
 * banner ma sie pokazac NA KAZDEJ STRONIE w identycznej formie.
 */
export function CookieConsent() {
    const decided = useHasConsent();
    const [open, setOpen] = useState(false);   // tryb "Dostosuj"
    const [current, setCurrent] = useState(() => getConsent());

    if (decided) return null;

    const save = () => {
        setConsent({ analytics: !!current.analytics, ads: !!current.ads });
    };

    const baseBtn = {
        padding: '8px 14px',
        borderRadius: '8px',
        fontSize: '13px',
        fontWeight: 600,
        cursor: 'pointer',
        border: 'none',
        whiteSpace: 'nowrap',
    };

    return (
        <div
            role="dialog"
            aria-label="Zgoda na pliki cookies"
            style={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 10000,
                background: 'white',
                borderTop: '1px solid #e2e8f0',
                boxShadow: '0 -8px 24px rgba(0,0,0,0.08)',
                padding: '16px',
                fontFamily: 'system-ui, sans-serif',
            }}
        >
            <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Cookie size={20} color="#2563eb" />
                        <strong style={{ fontSize: '14px', color: '#0f172a' }}>
                            Pliki cookies i prywatnosc
                        </strong>
                    </div>
                    <div style={{ flex: '1 1 320px', minWidth: '260px', fontSize: '13px', color: '#475569', lineHeight: 1.5 }}>
                        Uzywamy plikow cookies niezbednych do dzialania serwisu oraz, za Twoja zgoda, do analityki
                        (Google Analytics) i wyswietlania reklam (Google AdSense). Mozesz zaakceptowac wszystkie,
                        odrzucic opcjonalne lub dostosowac wybor. Szczegoly: <a href="/privacy" style={{ color: '#2563eb', textDecoration: 'underline' }}>Polityka prywatnosci</a>.
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <button
                            onClick={rejectAll}
                            style={{ ...baseBtn, background: '#f1f5f9', color: '#334155' }}
                        >
                            Tylko niezbedne
                        </button>
                        <button
                            onClick={() => setOpen(o => !o)}
                            style={{ ...baseBtn, background: 'transparent', color: '#475569', border: '1px solid #cbd5e1', display: 'flex', alignItems: 'center', gap: '4px' }}
                        >
                            Dostosuj {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                        <button
                            onClick={acceptAll}
                            style={{ ...baseBtn, background: '#2563eb', color: 'white' }}
                        >
                            Akceptuj wszystko
                        </button>
                    </div>
                </div>

                {open && (
                    <div style={{ marginTop: '14px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                        <ToggleRow
                            label="Niezbedne (zawsze aktywne)"
                            desc="Cookies wymagane do dzialania strony - logowanie, koszyk Zorki, bezpieczenstwo."
                            checked
                            disabled
                            onChange={() => { }}
                        />
                        <ToggleRow
                            label="Analityka (Google Analytics)"
                            desc="Anonimowe statystyki uzycia strony - pomagaja nam ulepszac serwis."
                            checked={!!current.analytics}
                            onChange={(v) => setCurrent(c => ({ ...c, analytics: v }))}
                        />
                        <ToggleRow
                            label="Reklamy (Google AdSense)"
                            desc="Personalizacja reklam przez Google. Bez tego widzisz reklamy niepersonalizowane."
                            checked={!!current.ads}
                            onChange={(v) => setCurrent(c => ({ ...c, ads: v }))}
                        />
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                            <button onClick={save} style={{ ...baseBtn, background: '#2563eb', color: 'white' }}>
                                Zapisz wybor
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ToggleRow({ label, desc, checked, disabled, onChange }) {
    return (
        <label style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '12px',
            padding: '8px 0',
            cursor: disabled ? 'default' : 'pointer',
        }}>
            <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: '13px', color: '#0f172a' }}>{label}</div>
                <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', lineHeight: 1.4 }}>{desc}</div>
            </div>
            <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={(e) => onChange(e.target.checked)}
                style={{ marginTop: '4px', width: '18px', height: '18px', cursor: disabled ? 'default' : 'pointer' }}
            />
        </label>
    );
}

export default CookieConsent;
